import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { authApiEndpoint } from "../../utils/env";
import { getMe, logLogin } from "../../api/adminApi";
import i18n from "../../i18n";

// Simple JWT payload decoder (no verification, client-side only)
function decodeJwt(token) {
  try {
    const [, payload] = token.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// Basic expiry check using JWT `exp` (seconds since epoch)
function isTokenExpired(token) {
  if (!token) return true;
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return false; // treat missing exp as non-expiring
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSeconds;
}

// Heuristic: decide if claims indicate an admin role.
function claimsIndicateAdmin(claims) {
  if (!claims && claims !== 0) return false;

  function normalize(val) {
    if (!val && val !== 0) return [];
    if (Array.isArray(val)) return val.map((v) => String(v));
    if (typeof val === "object") {
      // If object, look for role-like properties and return their values
      const keys = Object.keys(val || {});
      const out = [];
      for (const k of keys) {
        try {
          const v = val[k];
          if (v == null) continue;
          if (typeof v === "string") out.push(v);
          else if (Array.isArray(v)) out.push(...v.map((x) => String(x)));
        } catch {}
      }
      return out;
    }
    if (typeof val === "string") return [val];
    return [String(val)];
  }

  const arr = normalize(claims).map((s) => (s || "").toString());
  for (const v of arr) {
    if (!v) continue;
    const low = v.toLowerCase();
    // treat any role containing 'admin' as admin (covers DIXI_ADMIN)
    if (low.includes("admin")) return true;
    // also recognize common admin values
    if (low === "administrator" || low === "admin") return true;
  }
  return false;
}

const AUTH_USER_KEY = "auth_user"; // stored JSON user object
const AUTH_TOKEN_KEY = "auth_token"; // stored raw JWT token
const AUTH_REMEMBER_KEY = "auth_remember"; // '1' if persistent
const AUTH_CRED_KEY = "auth_saved_credentials"; // WARNING: stores last email & password (plain obfuscated)
const AUTH_REFRESH_TOKEN_KEY = "auth_refresh_token"; // stored refresh token (longer-lived)

// Backend endpoint (can promote to env var later)
const LOGIN_ENDPOINT = authApiEndpoint("/api/ApplicationUser/Login");
const REFRESH_ENDPOINT = authApiEndpoint("/api/ApplicationUser/Refresh");
// Windows SSO endpoints
const WINDOWS_SSO_LOGIN_ENDPOINT = authApiEndpoint("/api/WindowsAuth/Login");
const WINDOWS_SSO_STATUS_ENDPOINT = authApiEndpoint("/api/WindowsAuth/Status");

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [language, setLanguageState] = useState(
    (typeof localStorage !== "undefined" && localStorage.getItem("locale")) ||
      i18n?.language ||
      "en",
  );
  // Keep i18n in sync with auth provider language convenience API
  useEffect(() => {
    try {
      if (language && i18n && i18n.language !== language) {
        i18n.changeLanguage(language);
      }
    } catch {}
  }, [language]);

  const setLanguage = useCallback((lang) => {
    try {
      setLanguageState(lang);
      if (i18n) i18n.changeLanguage(lang);
      if (typeof localStorage !== "undefined")
        localStorage.setItem("locale", lang);
    } catch {}
  }, []);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remembered, setRemembered] = useState(false);

  // Simulate loading existing session (e.g., from localStorage/token)
  useEffect(() => {
    const persistent = localStorage.getItem(AUTH_REMEMBER_KEY) === "1";
    setRemembered(persistent);
    const storage = persistent ? localStorage : sessionStorage;
    const storedToken = storage.getItem(AUTH_TOKEN_KEY);
    const storedUser = storage.getItem(AUTH_USER_KEY);
    const storedRefreshToken = storage.getItem(AUTH_REFRESH_TOKEN_KEY);
    if (storedRefreshToken) setRefreshToken(storedRefreshToken);

    if (storedToken && isTokenExpired(storedToken)) {
      // Clear expired session data
      [localStorage, sessionStorage].forEach((s) => {
        try {
          s.removeItem(AUTH_TOKEN_KEY);
          s.removeItem(AUTH_USER_KEY);
          s.removeItem(AUTH_REFRESH_TOKEN_KEY);
        } catch {}
      });
      setToken(null);
      setUser(null);
      setLoading(false);
      return;
    }

    if (storedToken) setToken(storedToken);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        /* ignore */
      }
    } else if (storedToken) {
      const payload = decodeJwt(storedToken);
      if (payload) {
        const initialUser = {
          email: payload.email || payload.upn || payload.unique_name || "user",
          name: payload.name || payload.given_name || payload.email || "User",
          claims: payload,
        };
        setUser(initialUser);
        // Fetch server-driven roles/claims if possible
        (async () => {
          try {
            const me = await getMe(storedToken);
            // me contains { name, email, roles, claims, isAdmin }
            const merged = {
              ...initialUser,
              server: me,
              claims: me.claims ?? initialUser.claims,
              // treat server-provided isAdmin OR an admin-like role in either
              // the server claims or the original JWT payload as admin
              isAdmin:
                !!me.isAdmin ||
                claimsIndicateAdmin(me.claims) ||
                claimsIndicateAdmin(initialUser.claims),
            };
            setUser(merged);
            // persist updated user object
            try {
              storage.setItem(AUTH_USER_KEY, JSON.stringify(merged));
            } catch {}
          } catch {
            // ignore failures to contact /api/Me
          }
        })();
      }
    }
    setLoading(false);
  }, []);

  const refreshAccessToken = useCallback(async (refreshTokenValue) => {
    if (!refreshTokenValue) return null;
    let response;
    try {
      response = await fetch(REFRESH_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
      });
    } catch (networkErr) {
      return null;
    }

    if (!response.ok) return null;

    const data = await response.json().catch(() => ({}));
    const nextToken = data.token || data.Token || data.access_token || null;
    const nextRefreshToken = data.refreshToken || data.refresh_token || null;
    if (!nextToken) return null;
    return { token: nextToken, refreshToken: nextRefreshToken };
  }, []);

  const login = useCallback(async (email, password, remember = false) => {
    if (!email || !password) throw new Error("Email and password required");

    let response;
    try {
      response = await fetch(LOGIN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userName: email, // backend expects userName field
          password,
          fbToken: "", // provided sample has empty fbToken
        }),
      });
    } catch (networkErr) {
      throw new Error(
        "Can't reach the server. Check your connection or try again in a moment.",
      );
    }

    if (!response.ok) {
      let raw = await response.text().catch(() => "");
      let backendMsg = "";
      // Try to parse json { message: "..." }
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          backendMsg = parsed.message || parsed.error || raw;
        } catch {
          backendMsg = raw;
        }
      }

      const status = response.status;
      let friendly = "Unable to sign you in. Please try again.";
      const lower = (backendMsg || "").toLowerCase();
      if (status === 400 && lower.includes("username or password")) {
        friendly = "Incorrect email or password.";
      } else if (status === 400 && lower.includes("required")) {
        friendly = "Please fill in both email and password.";
      } else if (status === 401) {
        friendly = "Your session is not authorized. Please log in again.";
      } else if (status === 403) {
        friendly = "You don't have access to this application.";
      } else if (status >= 500) {
        friendly = "Server error while signing in. Please try again shortly.";
      } else if (lower.includes("locked")) {
        friendly = "Account locked. Please contact support.";
      }

      throw new Error(friendly);
    }

    const data = await response.json().catch(() => ({}));

    const receivedToken = data.token || data.Token || data.access_token;
    if (!receivedToken) {
      throw new Error("Token not present in response");
    }

    const receivedRefreshToken =
      data.refreshToken || data.refresh_token || null;

    const payload = decodeJwt(receivedToken);
    const newUser = {
      email: payload?.email || payload?.upn || email,
      name:
        payload?.name ||
        payload?.given_name ||
        (email.includes("@") ? email.split("@")[0] : email),
      claims: payload || null,
    };

    setToken(receivedToken);
    setRefreshToken(receivedRefreshToken || null);
    setUser(newUser);
    setRemembered(!!remember);
    // clear previous copies in both storages
    [localStorage, sessionStorage].forEach((s) => {
      try {
        s.removeItem(AUTH_TOKEN_KEY);
        s.removeItem(AUTH_USER_KEY);
      } catch {}
    });
    const target = remember ? localStorage : sessionStorage;
    target.setItem(AUTH_TOKEN_KEY, receivedToken);
    target.setItem(AUTH_USER_KEY, JSON.stringify(newUser));
    if (receivedRefreshToken)
      target.setItem(AUTH_REFRESH_TOKEN_KEY, receivedRefreshToken);
    if (remember) localStorage.setItem(AUTH_REMEMBER_KEY, "1");
    else localStorage.removeItem(AUTH_REMEMBER_KEY);

    // Persist last credentials for convenience ONLY if remember is chosen.
    // Password saved in localStorage is a security risk on shared machines.
    // Consider replacing with a secure credential vault in production.
    if (remember) {
      try {
        const payload = {
          email,
          // light obfuscation, NOT encryption
          password: btoa(password),
          ts: Date.now(),
        };
        localStorage.setItem(AUTH_CRED_KEY, JSON.stringify(payload));
      } catch {}
    } else {
      localStorage.removeItem(AUTH_CRED_KEY);
    }

    // Fire-and-forget: audit log entry for this user.
    // We intentionally do not block login UX on this.
    try {
      logLogin(receivedToken).catch(() => {});
    } catch {}
    // Enrich with server-driven roles/claims
    (async () => {
      try {
        const me = await getMe(receivedToken);
        const merged = {
          ...newUser,
          server: me,
          claims: me.claims ?? newUser.claims,
          // treat server-provided isAdmin OR an admin-like role in either
          // the server claims or the original JWT payload as admin
          isAdmin:
            !!me.isAdmin ||
            claimsIndicateAdmin(me.claims) ||
            claimsIndicateAdmin(newUser.claims),
        };
        setUser(merged);
        try {
          target.setItem(AUTH_USER_KEY, JSON.stringify(merged));
        } catch {}
      } catch {
        // ignore
      }
    })();
    return newUser;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    [localStorage, sessionStorage].forEach((s) => {
      try {
        s.removeItem(AUTH_USER_KEY);
        s.removeItem(AUTH_TOKEN_KEY);
        s.removeItem(AUTH_REFRESH_TOKEN_KEY);
      } catch {}
    });
    // If the user had chosen remember me, we keep credentials so form can prefill next visit.
    const hadRemember = localStorage.getItem(AUTH_REMEMBER_KEY) === "1";
    if (!hadRemember) {
      localStorage.removeItem(AUTH_CRED_KEY);
    }
    // We still clear the remember flag so a future login without selecting remember does not persist the session token.
    localStorage.removeItem(AUTH_REMEMBER_KEY);
    setRemembered(false);
  }, []);

  // Auto-refresh access token shortly before it expires while the user is logged in.
  useEffect(() => {
    if (!token || !refreshToken) return;
    const payload = decodeJwt(token);
    if (!payload || !payload.exp) return;

    const expiresAtMs = payload.exp * 1000;
    const now = Date.now();
    const refreshAt = expiresAtMs - 60 * 1000; // refresh 1 minute before expiry

    const scheduleRefresh = () => {
      (async () => {
        const result = await refreshAccessToken(refreshToken);
        if (!result || !result.token) {
          // could not refresh, force logout
          logout();
          return;
        }
        const { token: newToken, refreshToken: newRefresh } = result;
        setToken(newToken);
        const effectiveRefresh = newRefresh || refreshToken;
        setRefreshToken(effectiveRefresh);

        const persistent = localStorage.getItem(AUTH_REMEMBER_KEY) === "1";
        const storage = persistent ? localStorage : sessionStorage;
        try {
          storage.setItem(AUTH_TOKEN_KEY, newToken);
          if (effectiveRefresh)
            storage.setItem(AUTH_REFRESH_TOKEN_KEY, effectiveRefresh);
        } catch {}
      })();
    };

    if (refreshAt <= now) {
      scheduleRefresh();
      return;
    }

    const id = setTimeout(scheduleRefresh, refreshAt - now);
    return () => clearTimeout(id);
  }, [token, refreshToken, refreshAccessToken, logout]);

  // Check if Windows SSO is available
  const checkWindowsSsoStatus = useCallback(async () => {
    try {
      const response = await fetch(WINDOWS_SSO_STATUS_ENDPOINT, {
        method: "GET",
        credentials: "include", // Required for Windows auth
      });
      if (!response.ok) return { available: false };
      const data = await response.json();
      return {
        available: data.windowsAuthEnabled ?? false,
        isAuthenticated: data.isAuthenticated ?? false,
        windowsUser: data.windowsUser ?? null,
        configuredGroups: data.configuredGroups ?? [],
      };
    } catch {
      return { available: false };
    }
  }, []);

  // Windows SSO login - uses browser's Windows credentials automatically
  const windowsSsoLogin = useCallback(async (remember = true) => {
    let response;
    try {
      response = await fetch(WINDOWS_SSO_LOGIN_ENDPOINT, {
        method: "GET",
        credentials: "include", // Required for Windows Negotiate auth
        headers: {
          Accept: "application/json",
        },
      });
    } catch (networkErr) {
      throw new Error(
        "Can't reach the server for Windows SSO. Check your connection or try again.",
      );
    }

    if (!response.ok) {
      let backendMsg = "";
      const raw = await response.text().catch(() => "");
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          backendMsg = parsed.message || parsed.error || raw;
        } catch {
          backendMsg = raw;
        }
      }

      const status = response.status;
      let friendly = "Windows SSO authentication failed.";
      if (status === 401) {
        friendly =
          "Windows authentication failed. Please ensure you are on the corporate network.";
      } else if (status === 403) {
        friendly =
          "Access denied. Your Windows account is not authorized to use this application. Contact your administrator.";
      } else if (status >= 500) {
        friendly = "Server error during Windows SSO. Please try again shortly.";
      } else if (backendMsg) {
        friendly = backendMsg;
      }

      throw new Error(friendly);
    }

    const data = await response.json().catch(() => ({}));

    const receivedToken = data.token || data.Token || data.access_token;
    if (!receivedToken) {
      throw new Error("Token not present in SSO response");
    }

    const receivedRefreshToken =
      data.refreshToken || data.refresh_token || null;

    // Extract user info from response (backend provides enriched user data)
    const userData = data.user || {};
    const payload = decodeJwt(receivedToken);
    const newUser = {
      email: userData.email || payload?.email || payload?.Email || "",
      name:
        userData.fullName ||
        payload?.FullName ||
        payload?.name ||
        userData.userName ||
        "",
      windowsAccount: userData.windowsAccount || payload?.UserName || "",
      claims: payload || null,
      roles: userData.roles || [],
      adGroups: userData.adGroups || [],
      authMethod: "WindowsSSO",
      isAdmin:
        claimsIndicateAdmin(userData.roles) ||
        claimsIndicateAdmin(payload) ||
        (userData.roles || []).some((r) =>
          ["admin", "dixi_admin"].includes(r.toLowerCase()),
        ),
    };

    setToken(receivedToken);
    setRefreshToken(receivedRefreshToken || null);
    setUser(newUser);
    setRemembered(!!remember);

    // Clear previous copies in both storages
    [localStorage, sessionStorage].forEach((s) => {
      try {
        s.removeItem(AUTH_TOKEN_KEY);
        s.removeItem(AUTH_USER_KEY);
      } catch {}
    });
    const target = remember ? localStorage : sessionStorage;
    target.setItem(AUTH_TOKEN_KEY, receivedToken);
    target.setItem(AUTH_USER_KEY, JSON.stringify(newUser));
    if (receivedRefreshToken)
      target.setItem(AUTH_REFRESH_TOKEN_KEY, receivedRefreshToken);
    if (remember) localStorage.setItem(AUTH_REMEMBER_KEY, "1");
    else localStorage.removeItem(AUTH_REMEMBER_KEY);

    // No need to store credentials for Windows SSO

    // Fire-and-forget: audit log entry for this user
    try {
      logLogin(receivedToken).catch(() => {});
    } catch {}

    return newUser;
  }, []);

  const value = {
    user,
    token,
    login,
    windowsSsoLogin,
    checkWindowsSsoStatus,
    logout,
    loading,
    isAuthenticated: !!user,
    remembered,
    savedCredentialsKey: AUTH_CRED_KEY,
    language,
    setLanguage,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
